import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Modal,
  notification,
  DatePicker,
  Button,
  InputNumber,
  Upload,
  AutoComplete,
  Row,
  Col
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";

import axiosInstance from "../../../utilities/axios";

var __awaiter =
  (this && this.__awaiter) ||
  function (thisArg, _arguments, P, generator) {
    function adopt(value) {
      return value instanceof P
        ? value
        : new P(function (resolve) {
            resolve(value);
          });
    }
    return new (P || (P = Promise))(function (resolve, reject) {
      function fulfilled(value) {
        try {
          step(generator.next(value));
        } catch (e) {
          reject(e);
        }
      }
      function rejected(value) {
        try {
          step(generator['throw'](value));
        } catch (e) {
          reject(e);
        }
      }
      function step(result) {
        result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
      }
      step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
  };

const NewProductModal = ({ onChange, onClose, visible }) => {
  const [form] = Form.useForm();

  const [brand, setBrand] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [brandsAndFamilies, setBrandsAndFamilies] = useState({});

  useEffect(() => {
    axiosInstance
      .get("/products/product-families")
      .then((response) => {
        setBrandsAndFamilies(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  }, []);

  const onSubmit = (values) => {
    const formData = new FormData();
    formData.append("brand", values.brand);
    formData.append("family", values.family);
    formData.append("name", values.name);
    formData.append("style", values.style);
    formData.append("techLevel", values.techLevel);
    formData.append("msrp", values.msrp);
    formData.append("startDate", values.startDate.format("YYYY-MM-DD"));
    formData.append("endDate", values.endDate ? values.endDate.format("YYYY-MM-DD") : null);
    formData.append("description", values.description);

    if (photo && photo.length > 0) {
      formData.append("photo", photo[0]?.originFileObj);
    }

    axiosInstance
      .post("/products", formData)
      .then(() => {
        onChange();
        onClose();
        notification.success({
          message: "Success",
          description: "Product created successfully",
        });

        axiosInstance
          .get("/products/product-families")
          .then((response) => {
            setBrandsAndFamilies(response.data);
          })
          .catch((error) => {
            notification.error({
              message: "Error",
              description: error.message,
            });
          });
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: error.message,
        });
      });
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      notification.error({
        message: "Error",
        description: "You can only upload JPG/PNG file",
      });
      return Upload.LIST_IGNORE;
    }
    return file;
  };

  const handleChange = ({ fileList: newFileList }) => {
    setPhoto(newFileList);
    form.setFieldValue("photo", newFileList);
  };

  const onPreview = file =>
    __awaiter(void 0, void 0, void 0, function* () {
      let src = file.url;
      if (!src) {
        src = yield new Promise(resolve => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj);
          reader.onload = () => resolve(reader.result);
        });
      }
      const image = new Image();
      image.src = src;
      const imgWindow = window.open(src);
      imgWindow === null || imgWindow === void 0
        ? void 0
        : imgWindow.document.write(image.outerHTML);
    });

  const handleClose = () => {
    form.resetFields();
    setPhoto(null);
    onClose();
  };

  return (
    <Modal
      title="New Product"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        name="newProductForm"
        onFinish={onSubmit}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Brand"
              name="brand"
              rules={[{ required: true, message: "Please input a brand" }]}
            >
              <AutoComplete
                placeholder="Enter product brand"
                options={Object.keys(brandsAndFamilies).map(brand => ({ value: brand }))}
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
                onChange={value => setBrand(value)}
              >
                <Input />
              </AutoComplete>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Family"
              name="family"
              rules={[{ required: true, message: "Please input a family" }]}
            >
              <AutoComplete
                placeholder="Enter product family"
                options={brandsAndFamilies[brand]?.map(family => ({ value: family })) || []}
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              >
                <Input />
              </AutoComplete>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Product Line"
              name="name"
              rules={[{ required: true, message: "Please input a product line" }]}
            >
              <Input placeholder="Enter product line" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Style"
              name="style"
              rules={[{ required: true, message: "Please input a style" }]}
            >
              <Input placeholder="Enter product style" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Tech Level"
          name="techLevel"
          rules={[{ required: true, message: "Please input a tech level" }]}
        >
          <Select placeholder="Select a tech level">
            <Select.Option value="1">Standard</Select.Option>
            <Select.Option value="2">Advanced</Select.Option>
            <Select.Option value="3">Premium</Select.Option>
            <Select.Option value="4">Everyday Low Price</Select.Option>
            <Select.Option value="5">Premium Plus</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          hidden
          // rules={[{ required: true, message: "Please input a description" }]}
        >
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Enter product description"
          />
        </Form.Item>
        <Form.Item
          label="MSRP"
          name="msrp"
          rules={[{ required: true, message: "Please input an MSRP" }]}
        >
          <InputNumber prefix='$' style={{ width: '100%' }} step={0.01} defaultValue={0}/>
        </Form.Item>
        <Form.Item
          label="Start Date"
          name="startDate"
          rules={[{ required: true, message: "Please input an effective date" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          label="Upload Product Photo"
          name="photo"
          // rules={[{ required: true, message: "Please upload a product photo" }]}
          style={{ marginBottom: 0 }}
        >
          <ImgCrop aspect={1}>
            <Upload
              name="avatar"
              listType="picture-card"
              beforeUpload={beforeUpload}
              onChange={handleChange}
              maxCount={1}
              onPreview={onPreview}
              customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
            >
              {uploadButton}
            </Upload>
          </ImgCrop>
        </Form.Item>
        <Button type="primary" htmlType="submit" style={{ width: "100%", marginBottom: "10px", marginTop: "24px" }}>
          Add Product
        </Button>
      </Form>
    </Modal>
  );
};

export default NewProductModal;